import type { Locale } from '@shared/locales';

/**
 * Plain-language names for the permissions in contract/permissions.json, in
 * both languages. tests/unit/docs.test.ts fails when the contract gains a
 * permission or scope that has no label here.
 */
export const permissionLabels: Record<string, Record<Locale, string>> = {
  'views.share': { en: 'Share saved views', es: 'Compartir vistas guardadas' },
  'data.import': { en: 'Import data', es: 'Importar datos' },
  'data.export': { en: 'Export data', es: 'Exportar datos' },
  'portal.read': { en: 'View resident portal', es: 'Ver el portal de residentes' },
  'portal.invite': { en: 'Invite portal accounts', es: 'Invitar cuentas al portal' },
  'portal.manage': { en: 'Manage resident portal', es: 'Administrar el portal de residentes' },
  'workspace.read': { en: 'View the workspace', es: 'Ver el espacio de trabajo' },
  'workspace.update': { en: 'Change workspace details', es: 'Cambiar los datos del espacio de trabajo' },
  'workspace.delete': { en: 'Delete the workspace', es: 'Eliminar el espacio de trabajo' },
  'workspace.export': { en: 'Export the workspace data', es: 'Exportar los datos del espacio de trabajo' },
  'members.read': { en: 'See members', es: 'Ver a los miembros' },
  'members.invite': { en: 'Invite members', es: 'Invitar a miembros' },
  'members.update_role': { en: "Change a member's role", es: 'Cambiar el rol de un miembro' },
  'members.remove': { en: 'Remove members', es: 'Quitar a miembros' },
  'locations.read': { en: 'View locations', es: 'Ver ubicaciones' },
  'locations.manage': { en: 'Create and manage locations', es: 'Crear y administrar ubicaciones' },
  'files.read': { en: 'View files', es: 'Ver archivos' },
  'files.upload': { en: 'Upload files', es: 'Subir archivos' },
  'files.delete': { en: 'Delete files', es: 'Eliminar archivos' },
  'billing.read': { en: 'View billing', es: 'Ver la facturación' },
  'billing.manage': { en: 'Manage billing', es: 'Administrar la facturación' },
  'ai.use': { en: 'Use the AI assistant', es: 'Usar el asistente de IA' },
  'ai.configure': { en: 'Configure the AI assistant', es: 'Configurar el asistente de IA' },
  'audit.read': { en: 'Read the audit log', es: 'Consultar el registro de auditoría' },
  'notifications.read': { en: 'Read their own notifications', es: 'Leer sus propias notificaciones' },
  'api_keys.manage': { en: 'Manage API keys', es: 'Administrar claves de API' },
  'integrations.manage': { en: 'Manage integrations', es: 'Administrar integraciones' },
  'settings.manage': { en: 'Manage workspace settings', es: 'Administrar la configuración del espacio de trabajo' },
  'properties.read': { en: 'View properties', es: 'Ver propiedades' },
  'properties.manage': { en: 'Manage properties', es: 'Administrar propiedades' },
  'units.manage': { en: 'Manage units', es: 'Administrar unidades' },
  'people.manage': { en: 'Manage residents and contacts', es: 'Administrar residentes y contactos' },
  'leases.manage': { en: 'Manage leases', es: 'Administrar contratos' },
  'maintenance.manage': { en: 'Manage maintenance', es: 'Administrar el mantenimiento' },
  'documents.manage': { en: 'Manage documents', es: 'Administrar documentos' },
  'reports.read': { en: 'View reports', es: 'Ver informes' },
  'payments.collect_rent': { en: 'Collect rent', es: 'Cobrar la renta' },
};

export const scopeLabels: Record<string, Record<Locale, string>> = {
  'workspace:read': {
    en: 'Read the workspace, its members and locations',
    es: 'Leer el espacio de trabajo, sus miembros y ubicaciones',
  },
  'workspace:write': {
    en: 'Change workspace details and manage locations',
    es: 'Cambiar los datos del espacio de trabajo y administrar ubicaciones',
  },
  'files:read': { en: 'Read files', es: 'Leer archivos' },
  'files:write': { en: 'Upload files', es: 'Subir archivos' },
  'audit:read': { en: 'Read the audit log', es: 'Leer el registro de auditoría' },
};

export const scopeWords: Record<string, Record<Locale, string>> = {
  workspace: { en: 'workspace', es: 'espacio de trabajo' },
  location: { en: 'location', es: 'ubicación' },
  self: { en: 'own account', es: 'cuenta propia' },
};
